

export const dynamic = "force-dynamic";

import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { eventsApi, mastersApi, apiErrorMessage } from "@/lib/api";
import DashboardLayout from "@/app/components/DashboardLayout";
import Modal from "@/app/components/Modal";
import { useAuth } from "@/hooks/useAuth";
import { statusBadge, formatCurrencyFull } from "@/lib/data";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  List,
  Grid3X3,
  SlidersHorizontal,
  X,
  Search,
  MapPin,
  Users,
  Sparkles,
  Plus,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { formatDateLong, formatMonthYear, formatMonthShort, formatWeekdayShort, localToday } from "@/lib/dateFormat";
import AvailabilityChecker from "./AvailabilityChecker";
import AddEventModal from "@/app/events/components/AddEventModal";

interface Event {
  id: number;
  name: string;
  event_name?: string;
  start_date: string;
  end_date?: string | null;
  event_date?: string;
  client_id: number;
  client?: { id: number; name: string } | null;
  venue_id?: number;
  venue?: { id: number; name: string; location: string } | null;
  status: string;
  total_amount?: number | string;
  event_type?: string;
  type?: string;
  guest_count?: number;
  financials?: { net_revenue?: number | string } | null;
}

interface Venue {
  id: number;
  name: string;
}

interface EventType {
  id: number;
  name: string;
}

/** An event with its first and last day as local YYYY-MM-DD strings */
interface CalEvent {
  e: Event;
  start: string;
  end: string;
  name: string;
}

type ViewMode = "calendar" | "list";
type ListTab = "upcoming" | "past";

// The real event statuses (Event::STATUS_TRANSITIONS)
const STATUSES = ["draft", "confirmed", "in_progress", "completed", "cancelled"] as const;
const MONEY_ROLES = ["super_admin", "admin", "master"];

const STATUS_DOT: Record<string, string> = {
  draft: "bg-indigo-400",
  confirmed: "bg-emerald-500",
  in_progress: "bg-blue-500",
  completed: "bg-slate-400",
  cancelled: "bg-red-300",
};

/** Event name chips inside the month grid, coloured by status */
const STATUS_CHIP: Record<string, string> = {
  draft: "bg-indigo-50 text-indigo-800 hover:bg-indigo-100",
  confirmed: "bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  in_progress: "bg-blue-50 text-blue-800 hover:bg-blue-100",
  completed: "bg-slate-100 text-slate-600 hover:bg-slate-200",
  cancelled: "bg-red-50 text-red-400 line-through hover:bg-red-100",
};

const statusLabel = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "Unknown");
const statusPill = (s: string) => (s === "cancelled" ? "bg-red-50 text-red-700 border border-red-200" : statusBadge(s));
const statusOrder = (s: string) => ({ in_progress: 0, confirmed: 1, draft: 2, completed: 3, cancelled: 4 } as Record<string, number>)[s] ?? 5;

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The event's calendar day in local time. The API sends datetimes in UTC
 * ("2026-06-21T18:30:00Z" is 22 June in India), so never just slice the string.
 */
function dayKey(value?: string | null): string {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? value.slice(0, 10) : ymd(d);
}

/** Local Date from YYYY-MM-DD */
const fromKey = (key: string) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (key: string, n: number) => {
  const d = fromKey(key);
  d.setDate(d.getDate() + n);
  return ymd(d);
};

function dateRangeText(ce: CalEvent) {
  if (ce.start === ce.end) return formatDateLong(fromKey(ce.start)).replace(/^\w+, /, "");
  const s = fromKey(ce.start);
  const e = fromKey(ce.end);
  const sameMonth = ce.start.slice(0, 7) === ce.end.slice(0, 7);
  const short = (d: Date) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return sameMonth
    ? `${s.getDate()}–${short(e)} ${e.getFullYear()}`
    : `${short(s)} – ${short(e)} ${e.getFullYear()}`;
}

const num = (v: unknown) => Number(v) || 0;

export default function CalendarPage() {
  const { user } = useAuth();
  const canViewMoney = MONEY_ROLES.includes(user?.role || "");

  const [events, setEvents] = useState<Event[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? "list" : "calendar"
  );
  const [listTab, setListTab] = useState<ListTab>("upcoming");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Filters
  const [selectedVenue, setSelectedVenue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);

  const [showAvailabilityChecker, setShowAvailabilityChecker] = useState(false);
  const [addEventDefaults, setAddEventDefaults] = useState<{ venueId?: number; startDate?: string } | null>(null);
  const [showAddEvent, setShowAddEvent] = useState(false);

  const loadData = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      // Only the events are essential; the venue and type lists just fill the filters
      const [eventsRes, venuesRes, typesRes] = await Promise.all([
        eventsApi.list(),
        mastersApi.venues.list({ includeArchived: true }).catch(() => ({ data: [] })),
        mastersApi.eventTypes.list().catch(() => ({ data: [] })),
      ]);
      setEvents(eventsRes?.data?.data || eventsRes?.data || []);
      setVenues(venuesRes?.data?.data || venuesRes?.data || []);
      setEventTypes(typesRes?.data?.data || typesRes?.data || []);
      setLoadError(null);
    } catch (error) {
      setLoadError(apiErrorMessage(error, "Couldn't load events"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const today = localToday();

  // Every event with its local first/last day (end never before start)
  const calEvents = useMemo<CalEvent[]>(
    () =>
      events
        .map((e) => {
          const start = dayKey(e.start_date || e.event_date);
          const endRaw = dayKey(e.end_date) || start;
          return { e, start, end: endRaw < start ? start : endRaw, name: e.name || e.event_name || "Untitled event" };
        })
        .filter((ce) => ce.start),
    [events]
  );

  // Filters shared by the calendar and the list
  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return calEvents.filter(({ e, name }) => {
      if (e.status === "cancelled" && !showCancelled && selectedStatus !== "cancelled") return false;
      if (selectedStatus && e.status !== selectedStatus) return false;
      if (selectedVenue && String(e.venue_id ?? e.venue?.id ?? "") !== selectedVenue) return false;
      if (selectedType && e.event_type !== selectedType && e.type !== selectedType) return false;
      if (q) {
        const hay = [name, e.client?.name, e.venue?.name, e.event_type].map((v) => (v || "").toLowerCase());
        if (!hay.some((v) => v.includes(q))) return false;
      }
      return true;
    });
  }, [calEvents, searchQuery, showCancelled, selectedStatus, selectedVenue, selectedType]);

  const cancelledHidden = useMemo(
    () => (showCancelled || selectedStatus === "cancelled" ? 0 : calEvents.filter((ce) => ce.e.status === "cancelled").length),
    [calEvents, showCancelled, selectedStatus]
  );

  // ─── Month grid ───────────────────────────────────────────────────────────
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthStart = ymd(new Date(year, month, 1));
  const monthEnd = ymd(new Date(year, month, daysInMonth));
  const leadingBlanks = new Date(year, month, 1).getDay();
  const trailingBlanks = (7 - ((leadingBlanks + daysInMonth) % 7)) % 7;

  const sortForDay = (a: CalEvent, b: CalEvent) =>
    statusOrder(a.e.status) - statusOrder(b.e.status) || a.start.localeCompare(b.start) || a.name.localeCompare(b.name);

  /** Events overlapping the shown month, in date order */
  const monthEvents = useMemo(
    () =>
      visible
        .filter((ce) => ce.start <= monthEnd && ce.end >= monthStart)
        .sort((a, b) => a.start.localeCompare(b.start) || sortForDay(a, b)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, monthStart, monthEnd]
  );

  /** day (YYYY-MM-DD) → events on that day; multi-day events appear on every day they cover */
  const byDay = useMemo(() => {
    const map = new Map<string, CalEvent[]>();
    for (const ce of monthEvents) {
      let key = ce.start < monthStart ? monthStart : ce.start;
      const last = ce.end > monthEnd ? monthEnd : ce.end;
      while (key <= last) {
        const list = map.get(key);
        if (list) list.push(ce);
        else map.set(key, [ce]);
        key = addDays(key, 1);
      }
    }
    map.forEach((list) => list.sort(sortForDay));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthEvents, monthStart, monthEnd]);

  const dayEvents = (key: string) => visible.filter((ce) => ce.start <= key && ce.end >= key).sort(sortForDay);

  // ─── List view: from today onwards first, past reachable ─────────────────────
  const upcomingList = useMemo(
    () => visible.filter((ce) => ce.end >= today).sort((a, b) => a.start.localeCompare(b.start) || a.name.localeCompare(b.name)),
    [visible, today]
  );
  const pastList = useMemo(
    () => visible.filter((ce) => ce.end < today).sort((a, b) => b.end.localeCompare(a.end) || b.start.localeCompare(a.start)),
    [visible, today]
  );
  const listItems = listTab === "upcoming" ? upcomingList : pastList;

  // Month headings for the list
  const listGroups = useMemo(() => {
    const out: { key: string; label: string; items: CalEvent[] }[] = [];
    for (const ce of listItems) {
      const key = listTab === "upcoming" && ce.start < today ? today.slice(0, 7) : ce.start.slice(0, 7);
      const g = out.find((x) => x.key === key);
      if (g) g.items.push(ce);
      else out.push({ key, label: formatMonthYear(fromKey(`${key}-01`)), items: [ce] });
    }
    return out;
  }, [listItems, listTab, today]);

  const stats = useMemo(() => {
    const live = visible.filter((ce) => ce.e.status !== "cancelled");
    return {
      month: monthEvents.filter((ce) => ce.e.status !== "cancelled").length,
      today: live.filter((ce) => ce.start <= today && ce.end >= today).length,
      upcoming: live.filter((ce) => ce.start > today).length,
      past: live.filter((ce) => ce.end < today).length,
    };
  }, [visible, monthEvents, today]);

  const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const handleToday = () => {
    const d = new Date();
    setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setListTab("upcoming");
  };

  const resetFilters = () => {
    setSelectedVenue("");
    setSelectedStatus("");
    setSelectedType("");
    setSearchQuery("");
    setShowCancelled(false);
  };

  const extraFilterCount = [selectedStatus, selectedVenue, selectedType].filter(Boolean).length;
  const hasActiveFilters = !!(extraFilterCount || searchQuery || showCancelled);

  const openNewEvent = (startDate?: string) => {
    setAddEventDefaults({ startDate });
    setShowAddEvent(true);
  };

  const amountOf = (e: Event) => num(e.financials?.net_revenue ?? e.total_amount);

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const selectCls =
    "w-full h-10 px-3 rounded-xl border border-stone-200 text-sm bg-white text-stone-700 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

  // ─── Shared pieces ────────────────────────────────────────────────────────

  /** A tappable event card (list view, month list and the day popup) */
  const eventCard = (ce: CalEvent, compact = false) => {
    const { e } = ce;
    const cancelled = e.status === "cancelled";
    const start = fromKey(ce.start);
    const ongoing = ce.start < today && ce.end >= today;
    const isToday = ce.start === today || ongoing;
    const details = [e.client?.name, e.venue?.name, e.guest_count ? `${e.guest_count} guests` : ""].filter(Boolean).join(" · ");
    return (
      <Link
        key={`${e.id}-${compact ? "c" : "f"}`}
        to={`/events/detail?id=${e.id}`}
        className={`flex items-start gap-3 bg-white border rounded-xl ${compact ? "p-2.5" : "p-3 md:p-4"} min-h-[56px] transition-colors hover:border-amber-300 hover:bg-stone-50 ${
          isToday && !cancelled ? "border-amber-300" : "border-stone-200"
        } ${cancelled ? "opacity-70" : ""}`}
      >
        <div className={`flex-shrink-0 w-12 md:w-14 rounded-lg p-1.5 text-center ${isToday && !cancelled ? "bg-amber-50" : "bg-stone-50"}`}>
          <div className="text-[10px] md:text-xs font-semibold uppercase text-stone-500">{formatMonthShort(start)}</div>
          <div className="text-base md:text-lg font-bold text-stone-800 leading-tight">{start.getDate()}</div>
          <div className="text-[10px] text-stone-400">{formatWeekdayShort(start)}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`font-semibold text-stone-800 text-sm md:text-base truncate ${cancelled ? "line-through text-stone-500" : ""}`}>
              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${STATUS_DOT[e.status] || "bg-stone-300"}`} aria-hidden="true" />
              {ce.name}
            </p>
            <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium ${statusPill(e.status)}`}>
              {statusLabel(e.status)}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-stone-500 truncate">{details || "No client or venue yet"}</p>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <span className="text-[11px] md:text-xs text-stone-500">
              {dateRangeText(ce)}
              {!cancelled && isToday && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-600 text-white text-[10px] font-bold">{ongoing ? "Ongoing" : "Today"}</span>
              )}
            </span>
            {canViewMoney && !cancelled && amountOf(e) > 0 && (
              <span className="text-xs md:text-sm font-semibold text-stone-700 tabular-nums" title="Revenue after discount, before tax">
                {formatCurrencyFull(amountOf(e))}
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  // ─── Loading / error ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4" aria-label="Loading calendar" aria-busy="true">
          <div className="h-10 w-56 rounded-xl bg-stone-100 animate-pulse" />
          <div className="grid grid-cols-4 gap-2 md:gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-stone-100 animate-pulse" />)}
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-2 md:p-4">
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }, (_, i) => <div key={i} className="aspect-square md:aspect-auto md:h-24 rounded-lg bg-stone-100 animate-pulse" />)}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="font-semibold text-stone-800">Couldn't load the calendar</p>
          <p className="text-sm text-stone-500 mt-1">{loadError}</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-4 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50"
          >
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const monthLabel = formatMonthYear(currentDate);
  const selectedDayEvents = selectedDay ? dayEvents(selectedDay) : [];
  const kpi = (highlight = false) =>
    `text-center rounded-xl border px-2 py-2 md:py-3 transition-colors hover:border-amber-300 ${highlight ? "bg-amber-50 border-amber-300" : "bg-white border-stone-200"}`;

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-stone-800">Event Calendar</h1>
            <p className="text-xs md:text-sm text-stone-500 mt-0.5 hidden sm:block">Every booking by day, coloured by status</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAvailabilityChecker((v) => !v)}
              aria-pressed={showAvailabilityChecker}
              className={`h-10 inline-flex items-center gap-1.5 px-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
                showAvailabilityChecker ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
              title="Check venue availability"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Availability</span>
            </button>

            <div className="flex bg-stone-100 rounded-xl p-0.5" role="group" aria-label="View">
              <button
                type="button"
                onClick={() => setViewMode("calendar")}
                aria-pressed={viewMode === "calendar"}
                className={`w-10 h-9 inline-flex items-center justify-center rounded-lg transition-all ${
                  viewMode === "calendar" ? "bg-white shadow-sm text-amber-600" : "text-stone-500 hover:text-stone-700"
                }`}
                title="Month view"
                aria-label="Month view"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                className={`w-10 h-9 inline-flex items-center justify-center rounded-lg transition-all ${
                  viewMode === "list" ? "bg-white shadow-sm text-amber-600" : "text-stone-500 hover:text-stone-700"
                }`}
                title="List view"
                aria-label="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <button
              type="button"
              onClick={handleToday}
              className="h-10 px-3 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap"
            >
              Today
            </button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          <button type="button" onClick={() => setViewMode("calendar")} className={kpi()}>
            <div className="text-lg md:text-xl font-bold text-stone-800 tabular-nums">{stats.month}</div>
            <div className="text-[10px] md:text-xs text-stone-500 font-medium uppercase tracking-wide truncate">In {formatMonthShort(currentDate)}</div>
          </button>
          <button type="button" onClick={() => setSelectedDay(today)} className={kpi(stats.today > 0)}>
            <div className={`text-lg md:text-xl font-bold tabular-nums ${stats.today ? "text-amber-800" : "text-stone-800"}`}>{stats.today}</div>
            <div className="text-[10px] md:text-xs text-stone-500 font-medium uppercase tracking-wide">Today</div>
          </button>
          <button type="button" onClick={() => { setViewMode("list"); setListTab("upcoming"); }} className={kpi()}>
            <div className="text-lg md:text-xl font-bold text-stone-800 tabular-nums">{stats.upcoming}</div>
            <div className="text-[10px] md:text-xs text-stone-500 font-medium uppercase tracking-wide">Upcoming</div>
          </button>
          <button type="button" onClick={() => { setViewMode("list"); setListTab("past"); }} className={kpi()}>
            <div className="text-lg md:text-xl font-bold text-stone-800 tabular-nums">{stats.past}</div>
            <div className="text-[10px] md:text-xs text-stone-500 font-medium uppercase tracking-wide">Past</div>
          </button>
        </div>

        {/* Availability Checker Panel */}
        {showAvailabilityChecker && (
          <AvailabilityChecker
            onCreateEvent={(venueId, _venueName, startDate) => {
              setAddEventDefaults({ venueId, startDate });
              setShowAddEvent(true);
            }}
          />
        )}

        {/* Search & filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search event, client or venue…"
                aria-label="Search events"
                className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-white"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              aria-expanded={showFilters}
              className={`h-10 inline-flex items-center gap-1.5 px-3 rounded-xl border text-sm font-semibold ${
                showFilters || extraFilterCount ? "border-amber-400 bg-amber-50 text-amber-800" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {extraFilterCount ? ` (${extraFilterCount})` : ""}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-stone-200 bg-white text-sm text-stone-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                className="w-4 h-4 accent-amber-600"
              />
              Show cancelled{cancelledHidden ? ` (${cancelledHidden})` : ""}
            </label>
            {hasActiveFilters && (
              <button type="button" onClick={resetFilters} className="h-10 inline-flex items-center gap-1 px-3 rounded-xl text-sm text-stone-600 hover:bg-stone-100">
                <X className="w-4 h-4" /> Clear
              </button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white border border-stone-200 rounded-xl p-3">
              <div>
                <label htmlFor="cal-status" className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
                <select id="cal-status" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className={selectCls}>
                  <option value="">Any status</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cal-venue" className="block text-xs font-medium text-stone-500 mb-1.5">Venue</label>
                <select id="cal-venue" value={selectedVenue} onChange={(e) => setSelectedVenue(e.target.value)} className={selectCls}>
                  <option value="">Any venue</option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={String(venue.id)}>{venue.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cal-type" className="block text-xs font-medium text-stone-500 mb-1.5">Event type</label>
                <select id="cal-type" value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className={selectCls}>
                  <option value="">Any type</option>
                  {eventTypes.map((type) => (
                    <option key={type.id} value={type.name}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* ─── MONTH VIEW ─── */}
        {viewMode === "calendar" && (
          <>
            <div className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-2 py-1.5 md:px-3">
              <button type="button" onClick={handlePrevMonth} aria-label="Previous month" className="w-10 h-10 inline-flex items-center justify-center hover:bg-stone-100 rounded-lg">
                <ChevronLeft className="w-5 h-5 text-stone-600" />
              </button>
              <div className="text-center">
                <h2 className="text-base md:text-lg font-bold text-stone-800">{monthLabel}</h2>
                <p className="text-[11px] md:text-xs text-stone-500">
                  {monthEvents.length} event{monthEvents.length === 1 ? "" : "s"}
                </p>
              </div>
              <button type="button" onClick={handleNextMonth} aria-label="Next month" className="w-10 h-10 inline-flex items-center justify-center hover:bg-stone-100 rounded-lg">
                <ChevronRight className="w-5 h-5 text-stone-600" />
              </button>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-1.5 md:p-3">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayNames.map((d) => (
                  <div key={d} className="text-center font-semibold text-stone-500 py-1 text-[11px] md:text-xs">
                    <span className="md:hidden">{d.charAt(0)}</span>
                    <span className="hidden md:inline">{d}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: leadingBlanks }, (_, i) => (
                  <div key={`lead-${i}`} className="rounded-lg bg-stone-50 min-h-[52px] md:min-h-[112px]" />
                ))}

                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const key = ymd(new Date(year, month, day));
                  const list = byDay.get(key) || [];
                  const isToday = key === today;
                  const isPast = key < today;
                  const label = `${formatDateLong(fromKey(key))}: ${list.length ? `${list.length} event${list.length === 1 ? "" : "s"}` : "no events"}`;
                  const cellTone = isToday
                    ? "border-amber-400 bg-amber-50/60"
                    : `${list.length ? "border-stone-200" : "border-stone-100"} ${isPast ? "bg-white md:bg-stone-50/60" : "bg-white"}`;
                  const extra = list.length - 3;

                  return (
                    <div key={key} className={`relative rounded-lg border ${cellTone}`}>
                      {/* Phone: the whole cell is one button — dots coloured by status plus a count */}
                      <button
                        type="button"
                        onClick={() => setSelectedDay(key)}
                        aria-label={label}
                        className="md:hidden w-full min-h-[52px] flex flex-col items-center pt-1 pb-1 rounded-lg active:bg-stone-100"
                      >
                        <span className={`text-xs font-semibold ${isToday ? "text-amber-800" : isPast ? "text-stone-400" : "text-stone-700"}`}>{day}</span>
                        {list.length > 0 && (
                          <>
                            <span className="flex items-center gap-0.5 mt-1" aria-hidden="true">
                              {list.slice(0, 3).map((ce) => (
                                <span key={ce.e.id} className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[ce.e.status] || "bg-stone-300"}`} />
                              ))}
                            </span>
                            <span className="text-[10px] font-semibold text-stone-600 leading-none mt-0.5">{list.length}</span>
                          </>
                        )}
                      </button>

                      {/* Desktop: event names, "+N more" opens the day */}
                      <div className="hidden md:flex flex-col min-h-[112px] p-1.5 cursor-pointer" onClick={() => setSelectedDay(key)}>
                        <button
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); setSelectedDay(key); }}
                          aria-label={label}
                          className={`self-start w-7 h-7 rounded-full text-xs font-semibold inline-flex items-center justify-center hover:bg-stone-200 ${
                            isToday ? "bg-amber-500 text-white hover:bg-amber-600" : isPast ? "text-stone-400" : "text-stone-700"
                          }`}
                        >
                          {day}
                        </button>
                        <div className="mt-1 space-y-0.5 min-w-0">
                          {list.slice(0, 3).map((ce) => (
                            <Link
                              key={ce.e.id}
                              to={`/events/detail?id=${ce.e.id}`}
                              onClick={(ev) => ev.stopPropagation()}
                              title={`${ce.name} · ${statusLabel(ce.e.status)}${ce.e.client?.name ? ` · ${ce.e.client.name}` : ""}${ce.start < key ? " (continued)" : ""}`}
                              className={`block truncate rounded px-1.5 py-0.5 text-[11px] leading-4 font-medium ${STATUS_CHIP[ce.e.status] || "bg-stone-100 text-stone-700"}`}
                            >
                              {ce.name}
                            </Link>
                          ))}
                          {extra > 0 && (
                            <button
                              type="button"
                              onClick={(ev) => { ev.stopPropagation(); setSelectedDay(key); }}
                              className="block w-full text-left px-1.5 text-[11px] font-semibold text-stone-500 hover:text-amber-700 hover:underline"
                            >
                              +{extra} more
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {Array.from({ length: trailingBlanks }, (_, i) => (
                  <div key={`trail-${i}`} className="rounded-lg bg-stone-50 min-h-[52px] md:min-h-[112px]" />
                ))}
              </div>

              {/* Legend */}
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] md:text-xs justify-center text-stone-600">
                {STATUSES.filter((s) => s !== "cancelled" || showCancelled || selectedStatus === "cancelled").map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${STATUS_DOT[s]}`} />
                    <span className={s === "cancelled" ? "line-through" : ""}>{statusLabel(s)}</span>
                  </span>
                ))}
                <span className="text-stone-400 md:hidden">Tap a day to see its events</span>
              </div>
            </div>

            {/* Events in the shown month */}
            <div className="bg-white border border-stone-200 rounded-xl p-3 md:p-5">
              <h3 className="text-sm md:text-base font-bold text-stone-800 mb-3">
                Events in {monthLabel} <span className="text-stone-500 font-normal">({monthEvents.length})</span>
              </h3>
              {monthEvents.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                  <p className="text-sm text-stone-600 font-medium">
                    {hasActiveFilters ? "No events match these filters this month" : `Nothing booked in ${monthLabel}`}
                  </p>
                  {hasActiveFilters ? (
                    <button type="button" onClick={resetFilters} className="mt-2 h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">
                      Clear filters
                    </button>
                  ) : (
                    <button type="button" onClick={() => openNewEvent()} className="mt-3 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90">
                      <Plus className="w-4 h-4" /> New event
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">{monthEvents.map((ce) => eventCard(ce, true))}</div>
              )}
            </div>
          </>
        )}

        {/* ─── LIST VIEW ─── */}
        {viewMode === "list" && (
          <div className="space-y-3">
            <div className="flex gap-1 p-1 rounded-xl bg-stone-100 border border-stone-200 w-full sm:w-auto sm:inline-flex" role="group" aria-label="Which events">
              {([
                ["upcoming", `From today (${upcomingList.length})`],
                ["past", `Past (${pastList.length})`],
              ] as [ListTab, string][]).map(([tab, text]) => (
                <button
                  key={tab}
                  type="button"
                  aria-pressed={listTab === tab}
                  onClick={() => setListTab(tab)}
                  className={`flex-1 sm:flex-none h-9 px-4 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    listTab === tab ? "bg-white text-stone-900 font-semibold shadow-sm" : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  {text}
                </button>
              ))}
            </div>

            {listItems.length === 0 ? (
              <div className="text-center py-12 bg-white border border-stone-200 rounded-xl px-4">
                <CalendarIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-600 font-medium text-sm">
                  {hasActiveFilters
                    ? "No events match these filters"
                    : listTab === "upcoming" ? "Nothing booked from today on" : "No past events"}
                </p>
                {hasActiveFilters ? (
                  <button type="button" onClick={resetFilters} className="mt-2 h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">
                    Clear filters
                  </button>
                ) : listTab === "upcoming" ? (
                  <button type="button" onClick={() => openNewEvent()} className="mt-3 h-10 inline-flex items-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90">
                    <Plus className="w-4 h-4" /> New event
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="space-y-4">
                {listGroups.map((g) => (
                  <section key={g.key}>
                    <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-400 mb-2 px-1">
                      {g.label} <span className="font-normal">· {g.items.length}</span>
                    </h3>
                    <div className="space-y-2">{g.items.map((ce) => eventCard(ce))}</div>
                  </section>
                ))}
                {listTab === "upcoming" && pastList.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setListTab("past"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="w-full h-10 rounded-xl border border-stone-200 bg-white text-sm font-semibold text-stone-700 hover:bg-stone-50"
                  >
                    Show {pastList.length} past event{pastList.length === 1 ? "" : "s"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Events on the tapped day */}
      <Modal
        isOpen={selectedDay !== null}
        onClose={() => setSelectedDay(null)}
        title={selectedDay ? formatDateLong(fromKey(selectedDay)) : ""}
        size="md"
        footer={
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button type="button" onClick={() => setSelectedDay(null)} className="h-10 px-4 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
              Close
            </button>
            <button
              type="button"
              onClick={() => { const d = selectedDay || undefined; setSelectedDay(null); openNewEvent(d); }}
              className="h-10 inline-flex items-center justify-center gap-2 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90"
            >
              <Plus className="w-4 h-4" /> New event on this day
            </button>
          </div>
        }
      >
        {selectedDayEvents.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="w-10 h-10 text-stone-300 mx-auto mb-2" />
            <p className="text-sm text-stone-600 font-medium">Nothing booked on this day</p>
            {cancelledHidden > 0 && <p className="text-xs text-stone-500 mt-1">Cancelled events are hidden — tick “Show cancelled” to see them.</p>}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-stone-500 flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> {selectedDayEvents.length} event{selectedDayEvents.length === 1 ? "" : "s"}</span>
              <span className="inline-flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {selectedDayEvents.filter((ce) => ce.e.status !== "cancelled").reduce((s, ce) => s + num(ce.e.guest_count), 0).toLocaleString("en-IN")} guests
              </span>
              {new Set(selectedDayEvents.map((ce) => ce.e.venue?.name).filter(Boolean)).size > 0 && (
                <span className="inline-flex items-center gap-1 min-w-0 truncate">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  {[...new Set(selectedDayEvents.map((ce) => ce.e.venue?.name).filter(Boolean))].join(", ")}
                </span>
              )}
            </p>
            {selectedDayEvents.map((ce) => eventCard(ce, true))}
          </div>
        )}
      </Modal>

      {/* New event — from the availability checker, a day, or an empty state */}
      <AddEventModal
        isOpen={showAddEvent}
        onClose={() => { setShowAddEvent(false); setAddEventDefaults(null); }}
        onEventAdded={() => { setShowAddEvent(false); setAddEventDefaults(null); loadData(true); }}
        defaultVenueId={addEventDefaults?.venueId}
        defaultStartDate={addEventDefaults?.startDate}
      />
    </DashboardLayout>
  );
}
